import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Menu, X, ChevronRight, Crown, LogOut, Bell,
  User, Star, Heart, Hand, Calendar, Sparkles,
  HelpCircle, Shield, Languages, Mic, Users, TrendingUp,
  Home, Clock, Compass, Sun, Phone, Mail
} from 'lucide-react';

const formatMobile = (m) => (m ? m.replace(/^\+91(\d{5})(\d{5})$/, '+91 $1 $2') : '');

const ShubhHeader = ({ title, showMenu = true, showBack = false, onBack }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = Boolean(user?.is_admin);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const menuItems = [
    { icon: Bell, label: language === 'hi' ? 'रिमाइंडर' : 'Reminders', action: () => navigate('/reminders'), color: 'text-tulsi' },
    { 
      icon: Home, 
      label: language === 'hi' ? 'होम' : 'Home', 
      action: () => navigate('/dashboard'),
      color: 'text-ink-medium'
    },
    { 
      icon: User, 
      label: language === 'hi' ? 'मेरी प्रोफ़ाइल' : 'My Profile', 
      action: () => navigate('/profiles'),
      color: 'text-ink-medium'
    },
    { 
      icon: Sun, 
      label: language === 'hi' ? 'राशिफल' : 'Rashifal', 
      action: () => navigate('/rashifal'),
      color: 'text-saffron-dark'
    },
    { 
      icon: Clock, 
      label: language === 'hi' ? 'जीवन समयरेखा' : 'Life Timeline', 
      action: () => navigate('/timeline'),
      color: 'text-lotus'
    },
    { 
      icon: Mic, 
      label: language === 'hi' ? 'वैदिक गुरु' : 'Vedic Guru', 
      action: () => navigate('/vedic-guru'),
      color: 'text-orange-600',
      highlight: true,
      badge: 'Voice AI'
    },
    { 
      icon: Star, 
      label: language === 'hi' ? 'जन्म कुंडली' : 'Janam Kundli', 
      action: () => navigate('/janam-kundli'),
      color: 'text-saffron-dark'
    },
    { 
      icon: Heart, 
      label: language === 'hi' ? 'कुंडली मिलान' : 'Kundli Milan', 
      action: () => navigate('/kundli-milan'),
      color: 'text-lotus'
    },
    { 
      icon: Users, 
      label: language === 'hi' ? 'परिवार मिलान' : 'Family Compatibility', 
      action: () => navigate('/family-compatibility'),
      color: 'text-lotus'
    },
    { 
      icon: TrendingUp, 
      label: language === 'hi' ? 'वार्षिक भविष्य' : 'Yearly Forecast', 
      action: () => navigate('/yearly-forecast'),
      color: 'text-saffron-dark'
    },
    { 
      icon: Hand, 
      label: language === 'hi' ? 'हस्तरेखा' : 'Palm Reading', 
      action: () => navigate('/palm-reading'),
      color: 'text-tulsi'
    },
    { 
      icon: Calendar, 
      label: language === 'hi' ? 'हिंदू पंचांग' : 'Hindu Panchang', 
      action: () => navigate('/hindu-calendar'),
      color: 'text-sky-600'
    },
    { 
      icon: Crown, 
      label: language === 'hi' ? 'सदस्यता' : 'Subscription', 
      action: () => navigate('/subscription'),
      color: 'text-saffron-dark'
    },
    { 
      icon: Compass, 
      label: language === 'hi' ? 'सभी सुविधाएं' : 'Explore All', 
      action: () => navigate('/explore'),
      color: 'text-ink-medium'
    },
    { 
      icon: HelpCircle, 
      label: language === 'hi' ? 'सहायता' : 'Help', 
      action: () => navigate('/help'),
      color: 'text-ink-light'
    },
  ];

  // Add admin link if user is admin
  if (isAdmin) {
    menuItems.unshift({ 
      icon: Shield, 
      label: language === 'hi' ? 'एडमिन पैनल' : 'Admin Panel', 
      action: () => navigate('/admin'),
      color: 'text-[#C84B32]',
      badge: 'Admin',
      highlight: true
    });
  }

  return (
    <>
      <header className="shubh-header">
        <div className="shubh-header-inner">
          {/* Left - Menu or Back */}
          <div className="flex items-center gap-3">
            {showBack ? (
              <button 
                onClick={onBack || (() => navigate(-1))}
                className="shubh-btn-icon shubh-btn-ghost"
                aria-label="Go back"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
            ) : showMenu ? (
              <button
                onClick={() => setMenuOpen(true)}
                className="shubh-btn-icon shubh-btn-ghost"
                data-testid="menu-button"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-ink-dark" style={{ color: 'var(--ink-dark)' }} />
              </button>
            ) : null}
          </div>

          {/* Center - Title */}
          <h1 className="shubh-header-title">
            {title || 'Nakshatra'}
          </h1>

          {/* Right - Language Toggle */}
          <button data-testid="header-reminders-button" aria-label="Reminders" title="Reminders" onClick={() => navigate('/reminders')} className="shubh-btn-icon shubh-btn-ghost"><Bell className="w-5 h-5" /></button>
          <button
            onClick={toggleLanguage}
            className="shubh-btn-sm shubh-btn-ghost flex items-center gap-1.5"
            style={{ 
              background: 'var(--warm-beige)',
              borderRadius: 'var(--radius-full)',
              padding: '6px 12px',
              minHeight: 'auto'
            }}
            data-testid="language-toggle"
          >
            <Languages className="w-4 h-4" style={{ color: 'var(--ink-medium)' }} />
            <span 
              className="text-sm font-medium"
              style={{ color: language === 'hi' ? 'var(--vermilion)' : 'var(--ink-dark)' }}
            >
              {language === 'en' ? 'EN' : 'हि'}
            </span>
          </button>
        </div>
      </header>

      {/* Slide-out Menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]"
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-[300px] z-[90] bg-white shadow-xl"
              style={{ 
                paddingTop: 'env(safe-area-inset-top)',
                background: 'var(--shubh-ivory)'
              }}
            >
              {/* Menu Header */}
              <div 
                className="p-5 border-b"
                style={{ borderColor: 'var(--border-light)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ 
                        background: 'linear-gradient(135deg, var(--saffron) 0%, var(--vermilion) 100%)'
                      }}
                    >
                      <span className="text-white text-lg font-bold">
                        {getInitials(user?.name)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold flex items-center gap-2" style={{ color: 'var(--ink-dark)' }} data-testid="menu-user-name">
                        <span className="truncate">{user?.name || 'User'}</span>
                        {isAdmin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-[#C84B32] text-white" data-testid="menu-admin-badge">ADMIN</span>
                        )}
                      </p>
                      {user?.mobile && (
                        <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: 'var(--ink-muted)' }} data-testid="menu-user-mobile">
                          <Phone className="w-3 h-3" /> {formatMobile(user.mobile)}
                        </p>
                      )}
                      {user?.email && (
                        <p className="text-xs flex items-center gap-1 mt-0.5 truncate" style={{ color: 'var(--ink-muted)' }} data-testid="menu-user-email">
                          <Mail className="w-3 h-3" /> {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => setMenuOpen(false)} 
                    className="p-2 rounded-full transition-colors"
                    style={{ background: 'var(--border-light)' }}
                  >
                    <X className="w-5 h-5" style={{ color: 'var(--ink-medium)' }} />
                  </button>
                </div>

                {/* Subscription Badge */}
                <button
                  onClick={() => { navigate('/subscription'); setMenuOpen(false); }}
                  className="w-full rounded-xl p-3 border transition-all hover:shadow-md"
                  style={{ 
                    background: 'linear-gradient(135deg, var(--warm-beige) 0%, var(--shubh-ivory) 100%)',
                    borderColor: 'rgba(245, 185, 66, 0.3)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5" style={{ color: 'var(--saffron-dark)' }} />
                      <div className="text-left">
                        <p className="text-sm font-medium" style={{ color: 'var(--ink-dark)' }}>
                          {user?.subscription_tier && user.subscription_tier !== 'free'
                            ? `${user.subscription_tier.charAt(0).toUpperCase()}${user.subscription_tier.slice(1)} Plan`
                            : 'Free Plan'}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                          {user?.subscription_tier === 'cosmic' ? 'All features unlocked' : 'Upgrade for more features'}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--saffron-dark)' }} />
                  </div>
                </button>
              </div>

              {/* Menu Items */}
              <div className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 250px)' }}>
                {menuItems.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      item.action();
                      setMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      item.highlight 
                        ? 'border'
                        : ''
                    }`}
                    style={{
                      background: item.highlight 
                        ? 'linear-gradient(135deg, rgba(234, 88, 12, 0.08) 0%, rgba(245, 185, 66, 0.08) 100%)'
                        : 'transparent',
                      borderColor: item.highlight ? 'rgba(234, 88, 12, 0.2)' : 'transparent'
                    }}
                    data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                    <span 
                      className="text-sm font-medium flex-1 text-left"
                      style={{ color: item.highlight ? 'var(--vermilion)' : 'var(--ink-dark)' }}
                    >
                      {item.label}
                    </span>
                    {item.badge && (
                      <span 
                        className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        style={{
                          background: 'rgba(234, 88, 12, 0.15)',
                          color: 'var(--vermilion)'
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}

                {/* Logout */}
                <div className="pt-4 mt-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
                    style={{ background: 'rgba(239, 68, 68, 0.08)' }}
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium text-red-500">
                      {language === 'hi' ? 'लॉग आउट' : 'Logout'}
                    </span>
                  </button>
                </div>
              </div>
              
              {/* Footer */}
              <div 
                className="absolute bottom-0 left-0 right-0 p-4 border-t"
                style={{ 
                  borderColor: 'var(--border-light)',
                  background: 'var(--shubh-ivory)'
                }}
              >
                <p className="text-center text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                  Nakshatra 2.0 • Made with devotion in India
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShubhHeader;
